import React, { ChangeEvent, useEffect, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import { FieldError, useFormContext, UseFormSetValue } from 'react-hook-form';
import { DropdownWithDeletedValueTemplate } from 'utils/DropdownWithDeletedValueTemplate';

import {
  DropdownSelectorType,
  Employee,
  FieldFilling,
  RoleUI
} from '../types/interfaces';
import { StatusTransition } from '../types/interfaces/StatusTransition';
import { getFieldName } from '../utils/getFieldName';

import './css/TaskStatusComponent.css';

import FieldFillingControl from './FieldFillingControl';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';

export type FormField =
  | 'id'
  | 'key'
  | 'taskType'
  | 'name'
  | 'statusTransition'
  | `statusTransition.${number}`
  | `statusTransition.${number}.id`
  | `statusTransition.${number}.name`
  | 'actionName'
  | 'priority'
  | 'visibleOnMap';

interface TaskStatusComponentProps {
  statuses: DropdownSelectorType[];
  userRole: RoleUI[];
  statusTransition: StatusTransition;
  passedClassName: string;
  setValue: UseFormSetValue<any>;
  onSelectionChange: (selection: RoleUI[] | Employee[]) => void;
  onInputChange: (value: string | boolean, property: FormField) => void;
  onDropdownChange: (
    selectedValue: DropdownSelectorType | null,
    property: 'fromTaskStatus' | 'toTaskStatus'
  ) => void;
  onDelete: () => void;
  onTaskStatusUpdate: (
    taskId: string | number,
    updatedData: FieldFilling[]
  ) => void;
  errors?: {
    name?: FieldError;
    fromTaskStatus?: FieldError;
    toTaskStatus?: FieldError;
  };
}

function getTemplate() {
  const templateArray: FieldFilling[] = [];
  for (let index = 1; index <= 14; index++) {
    const item: FieldFilling = {
      id: 0,
      isActive: false,
      fieldType: index,
      name: getFieldName(index),
      isRequired: false,
      isResettable: false,
      statusTransitionId: 0,
      currentFunctions: [],
    };
    templateArray.push(item);
  }
  return templateArray;
}

export const TaskStatusComponent: React.FC<TaskStatusComponentProps> = ({
  statusTransition,
  passedClassName,
  onSelectionChange,
  onInputChange,
  onDropdownChange,
  onDelete,
  onTaskStatusUpdate,
  errors,
  statuses,
  userRole
}) => {
  const { trigger } = useFormContext();
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [confirmDialogVisible, setConfirmDialogVisible] =
    useState<boolean>(false);
  const [selectedFieldFilling, setSelectedFieldFilling] = useState<
    FieldFilling[]
  >([]);
  const FieldFillingControlRef = useRef<null | { handleCancel: () => void }>(
    null
  );
  const filterOutStatus = (statusToFilterFrom: keyof StatusTransition) => {
    return statuses.filter(
      (status) =>
        status.id !==
        (statusTransition[statusToFilterFrom] as DropdownSelectorType)?.id
    );
  };

  const priorityOptions = [
    { label: 'Низкий приоритет', value: 0 },
    { label: 'Средний приоритет', value: 1 },
    { label: 'Высокий приоритет', value: 2 }
  ];

  useEffect(() => {
    setSelectedFieldFilling(
      statusTransition && statusTransition.fieldFillingControls.length > 0
        ? statusTransition.fieldFillingControls
          .map((x) => ({
            ...x,
            name: getFieldName(x.fieldType)
          }))
          .sort((x) => x.fieldType)
        : getTemplate()
    );
  }, [statusTransition]);
  const handleActionNameChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onInputChange(value, 'name');
    // Trigger validation for this specific field
  };

  const handleStatusBeforeChange = async (e: DropdownChangeEvent) => {
    const selectedValue = e.value ?? null;

    onDropdownChange(selectedValue, 'fromTaskStatus');
    await trigger('statusTransition');
  };

  const handleStatusAfterChange = async (e: DropdownChangeEvent) => {
    const selectedValue = e.value ?? null;
    onDropdownChange(selectedValue, 'toTaskStatus');
    await trigger('statusTransition');
  };

  const toggleDialogVisibility = () => {
    setDialogVisible(true);
  };

  const handleUpdate = (updatedData: FieldFilling[]) => {
    statusTransition.fieldFillingControls = updatedData || [];
    setSelectedFieldFilling(updatedData);
    onTaskStatusUpdate(
      statusTransition.guid!,
      statusTransition.fieldFillingControls
    );
  };

  const closeDialog = () => {
    setDialogVisible(false);
  };

  const handleDeleteAll = () => {
    onDelete();
    setConfirmDialogVisible(false);
  };

  const onDialogHide = () => {
    if (FieldFillingControlRef.current !== null) {
      FieldFillingControlRef.current.handleCancel();
    }
  };

  const priorityValueTemplate = (option: DropdownSelectorType, props: any) => {
    if (!props.value && props.value !== 0) {
      return <span>Выберите приоритет</span>;
    }

    const selectedOption = props.options.find(
      (o: any) => o.value === props.value
    );
    return (
      <span>
        {selectedOption ? selectedOption.label : 'Выберите приоритет'}
      </span>
    );
  };

  return (
    <div
      className={
        passedClassName +
        ' task-status flex flex-row justify-content-between w-full gap-2'
      }
    >
      <Dialog
        header="Контроль заполнения полей"
        visible={dialogVisible}
        style={{
          width: '960px',
          minWidth: '960px',
          height: '90vh',
          minHeight: '550px'
        }}
        maximizable
        modal
        onHide={() => onDialogHide()}
      >
        <FieldFillingControl
          key={JSON.stringify(statusTransition)}
          fieldFillings={selectedFieldFilling}
          onUpdate={handleUpdate}
          closeDialog={closeDialog}
          ref={FieldFillingControlRef}
        />
      </Dialog>

      <DeleteConfirmationDialog
        visible={confirmDialogVisible}
        onHide={() => setConfirmDialogVisible(false)}
        onConfirm={handleDeleteAll}
      >
        <p>
          Вы уверены, что хотите удалить действие
          <strong>
            {statusTransition?.name?.length > 0
              ? ' ' + statusTransition.name
              : ''}
          </strong>
          ?
          <br />
          Данную операцию невозможно отменить.
        </p>
      </DeleteConfirmationDialog>

      <InputText
        placeholder="Название действия"
        className={`task-status--item ${
          errors?.name ? 'input-invalid' : 'input-valid'
        }`}
        value={statusTransition.name}
        onChange={handleActionNameChange}
      />

      <Dropdown
        value={statusTransition.fromTaskStatus}
        onChange={handleStatusBeforeChange}
        options={filterOutStatus('toTaskStatus')}
        placeholder={
          statusTransition.fromTaskStatus?.label
            ? statusTransition.fromTaskStatus?.label
            : 'Статус до'
        }
        className={`task-status--item fixed-dropdown ${
          errors?.fromTaskStatus ? 'input-invalid' : 'input-valid'
        }`}
        showClear
        filter
        tooltip={
          statusTransition.fromTaskStatus?.label &&
          statusTransition.fromTaskStatus.label.length > 19
            ? statusTransition.fromTaskStatus.label
            : undefined
        }
        tooltipOptions={{
          position: 'top',
          showDelay: 800
        }}
        valueTemplate={(option) => (
          <DropdownWithDeletedValueTemplate 
            option={option} 
            selectedItem={statusTransition.fromTaskStatus}
            placeholder={'Статус до'}
          />
        )}
      />

      <Dropdown
        value={statusTransition.toTaskStatus}
        onChange={handleStatusAfterChange}
        options={filterOutStatus('fromTaskStatus')}
        placeholder={
          statusTransition.toTaskStatus?.label
            ? statusTransition.toTaskStatus?.label
            : 'Статус после'
        }
        className={`task-status--item fixed-dropdown ${
          errors?.toTaskStatus ? 'input-invalid' : 'input-valid'
        }`}
        tooltip={
          statusTransition.toTaskStatus?.label &&
          statusTransition.toTaskStatus.label.length > 19
            ? statusTransition.toTaskStatus.label
            : undefined
        }
        showClear
        filter
        tooltipOptions={{
          position: 'top',
          showDelay: 800
        }}
        valueTemplate={(option) => (
          <DropdownWithDeletedValueTemplate 
            option={option} 
            selectedItem={statusTransition.toTaskStatus}
            placeholder={'Статус после'}
          />
        )}
      />

      {/* <div className="task-status--item flex flex-column">
        <div className="flex justify-content-between align-items-center gap-3">
          <DataTableDropdown
            userRole={userRole!}
            overlayWidthMultiplier={1.5}
            className={'pl-3 pr-3'}
            onSelectionChange={onSelectionChange}
            fixedLabel="Выбор ролей"
          />
        </div>
      </div> */}


      <Dropdown
        value={statusTransition.priority}
        options={priorityOptions}
        onChange={(e) => {
          onInputChange(e.value, 'priority');
        }}
        valueTemplate={priorityValueTemplate}
        optionLabel="label"
        placeholder="Выберите приоритет"
        className="task-status--item w-full md:w-14rem"
        showClear
      />

      <div className="flex align-items-center">
        <Checkbox
          checked={statusTransition.visibleOnMap || false}
          onChange={() => {
            const value = !statusTransition.visibleOnMap;
            onInputChange(value, 'visibleOnMap');
          }}
        />
        <label
          style={{ marginLeft: '7px' }}
        >
          Показать на карте 
        </label>
      </div>
      <Button
        className="mr-2"
        rounded
        severity="secondary"
        icon="pi pi-cog"
        onClick={toggleDialogVisibility}
        tooltip="Контроль заполнения полей"
      />

      <Button
        icon="pi pi-trash"
        onClick={() => setConfirmDialogVisible(true)} // Show confirmation dialog
        className="p-button-danger"
      />
    </div>
  );
};
